import { useSyncExternalStore } from "react"

/** A signature request put aside with Minimize, waiting to be restored. */
export interface MinimizedWizard {
  /** The wizard's own address, so Restore lands on the same step. */
  url: string
  title: string
  /** Null for a request not saved as a draft yet. */
  envelopeId: string | null
  stepNumber: number
}

const KEY = "esign.minimizedWizard"
const listeners = new Set<() => void>()

// Kept for the tab's life: a reload still offers it back. Storage can refuse
// (a private window), in which case it lives in memory only.
let current: MinimizedWizard | null = (() => {
  try {
    const raw = sessionStorage.getItem(KEY)
    return raw ? (JSON.parse(raw) as MinimizedWizard) : null
  } catch {
    return null
  }
})()

const publish = (next: MinimizedWizard | null) => {
  current = next
  try {
    if (next) sessionStorage.setItem(KEY, JSON.stringify(next))
    else sessionStorage.removeItem(KEY)
  } catch {
    // Memory still holds it.
  }
  listeners.forEach((listener) => listener())
}

export const minimizeWizard = (wizard: MinimizedWizard) => publish(wizard)

export const clearMinimizedWizard = () => publish(null)

export const useMinimizedWizard = () =>
  useSyncExternalStore(
    (listener) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    () => current
  )
